import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_BASE_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

export const casesAPI = {
  getCases: () => api.get('/cases'),
  getCase: (id) => api.get(`/cases/${id}`),
  createCase: (data) => api.post('/cases', data),
  createClientCase: (data) => api.post('/cases/client', data),
  updateCase: (id, data) => api.put(`/cases/${id}`, data),
  deleteCase: (id) => api.delete(`/cases/${id}`),
  cloneCase: (id) => api.post(`/cases/${id}/clone`),
  attorneyApprove: (id) => api.patch(`/cases/${id}/attorney-approve`),
  updateStatus: (id, status) => api.patch(`/cases/${id}/status`, null, { params: { status } }),
  reassignCase: (id, newManagerId) => api.patch(`/cases/${id}/reassign`, null, { params: { new_manager_id: newManagerId } }),
  getTimeline: (id) => api.get(`/cases/${id}/timeline`),
  getDocuments: (id) => api.get(`/cases/${id}/documents`),
  getTasks: (id) => api.get(`/cases/${id}/tasks`),
  getNotes: (id) => api.get(`/cases/${id}/notes`),
  getReport: (id) => api.get(`/cases/${id}/report`),
};

export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getFinancialSummary: () => api.get('/admin/financial-summary'),
  getAdminList: () => api.get('/admins/list'),
  getCasesByFilter: (filterType) => api.get('/admin/cases/by-filter', { params: { filter_type: filterType } }),
};

export const documentsAPI = {
  upload: (formData) => api.post('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadWithAnalysis: (formData) => api.post('/documents/upload-with-analysis', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  submitForAdminReview: (caseId, filename) => api.post(`/documents/${caseId}/${filename}/submit-for-review`),
  getDocuments: (caseId) => api.get(`/cases/${caseId}/documents`)
};

export const tasksAPI = {
  create: (data) => api.post('/tasks', data),
  updateStatus: (id, status) => api.patch(`/tasks/${id}/status`, null, { params: { status } }),
};

export const notesAPI = {
  create: (data) => api.post('/notes', data),
};

export const messagesAPI = {
  getMessages: (caseId) => api.get(`/messages/${caseId}`),
  createMessage: (data) => api.post('/messages', data),
  markAsRead: (messageId) => api.patch(`/messages/${messageId}/read`),
  getUnreadCount: () => api.get('/messages/unread/count'),
  getUnreadByCase: () => api.get('/messages/unread/by-case'),
  markAllCaseMessagesRead: (caseId) => api.post(`/messages/${caseId}/mark-all-read`),
};

export const timelineAPI = {
  create: (data) => api.post('/timeline', data),
};

export const referralAPI = {
  createAgreement: (data) => api.post('/referrals/agreements', data),
  getAgreements: () => api.get('/referrals/agreements'),
  submitMerchant: (data) => api.post('/referrals/merchants', data),
  getMerchants: () => api.get('/referrals/merchants'),
  acceptMerchant: (merchantId) => api.patch(`/referrals/merchants/${merchantId}/accept`),
  rejectMerchant: (merchantId) => api.patch(`/referrals/merchants/${merchantId}/reject`),
  getDashboardStats: () => api.get('/referrals/dashboard-stats'),
  getReferralsByFilter: (filterType) => api.get('/referrals/by-filter', { params: { filter_type: filterType } }),
  getMerchantPdf: (merchantId) => api.get(`/referrals/merchants/${merchantId}/pdf`, { responseType: 'blob' }),
  getAgreementPdf: (agreementId) => api.get(`/referrals/agreements/${agreementId}/pdf`, { responseType: 'blob' }),
  adminSignReferral: (merchantId, data) => api.post(`/referrals/merchants/${merchantId}/admin-sign`, data),
};

export const signaturesAPI = {
  // Signers management
  getSigners: () => api.get('/signatures/signers'),
  addSigner: (data) => api.post('/signatures/signers', data),
  toggleSigner: (signerId) => api.patch(`/signatures/signers/${signerId}/toggle`),
  deleteSigner: (signerId) => api.delete(`/signatures/signers/${signerId}`),
  
  // Document signing
  signDocument: (data) => api.post('/signatures/sign', data),
  getSignedDocuments: (status) => api.get('/signatures/documents', { params: status ? { status } : {} }),
  getSignedDocument: (documentId) => api.get(`/signatures/documents/${documentId}`),
  getDocumentPdf: (documentId) => api.get(`/signatures/documents/${documentId}/pdf`, { responseType: 'blob' }),
  getPendingReview: () => api.get('/signatures/pending-review'),
  getSignatureFonts: () => api.get('/signatures/fonts'),
};

export const userManagementAPI = {
  // User management (Super Admin)
  getAllUsers: () => api.get('/users/all'),
  getAdminUsers: () => api.get('/users/admins'),
  getUser: (userId) => api.get(`/users/${userId}`),
  createUser: (data) => api.post('/users/create', data),
  updateUser: (userId, data) => api.patch(`/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/users/${userId}`),
  resetPassword: (userId, newPassword) => api.post(`/users/${userId}/reset-password?new_password=${encodeURIComponent(newPassword)}`),
  changePassword: (data) => api.post('/users/change-password', data),
  
  // Login notifications
  getLoginNotifications: () => api.get('/users/notifications/logins'),
  markNotificationRead: (notificationId) => api.patch(`/users/notifications/${notificationId}/read`),
  clearNotifications: () => api.delete('/users/notifications/clear'),
};

export const poaAPI = {
  // Submit POA form (client)
  submitPOA: (data) => api.post('/poa/submit', data),
  
  // Admin sign POA
  adminSignPOA: (data) => api.post('/poa/admin/sign', data),
  
  // Get all POA documents
  getAllPOA: (status) => api.get('/poa/', { params: status ? { status } : {} }),
  
  // Get POA for specific case
  getPOAForCase: (caseId) => api.get(`/poa/case/${caseId}`),
  
  // Get specific POA document
  getPOA: (poaId) => api.get(`/poa/${poaId}`),
  
  // Get POA PDF
  getPOAPdf: (poaId) => api.get(`/poa/${poaId}/pdf`, { responseType: 'blob' }),
  
  // Get pending POA reviews (admin)
  getPendingReviews: () => api.get('/poa/pending-review/list'),
  
  // Auto-save POA draft
  saveDraft: (data) => api.post('/poa/draft/save', data),
  
  // Get POA draft for a case
  getDraft: (caseId) => api.get(`/poa/draft/${caseId}`),
};

export const templatesAPI = {
  // Get all templates
  getTemplates: () => api.get('/templates/'),
  
  // Get specific template
  getTemplate: (templateId) => api.get(`/templates/${templateId}`),
  
  // Assign template to client
  assignTemplate: (templateId, caseId, clientId) => 
    api.post(`/templates/${templateId}/assign?case_id=${caseId}&client_id=${clientId}`),
  
  // Get assigned documents for a client
  getAssignedDocuments: (clientId) => api.get(`/templates/assigned/${clientId}`),
  
  // Seed default templates
  seedDefaults: () => api.post('/templates/seed-defaults'),
};
